'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  WorkspaceMemberRole,
  type WorkspaceRoleListItemDto,
  WorkspacesApi,
} from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { type RoleAssignment } from '@/core/authz/role-assignment';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { RoleSelect } from '@/core/components/workspace/roles/role-select';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  roles: WorkspaceRoleListItemDto[];
  currentUserRole: WorkspaceMemberRole;
  onSuccess: () => void;
  dict: {
    title: string;
    description: string;
    emailLabel: string;
    emailPlaceholder: string;
    roleLabel: string;
    roleDescription: string;
    roleSelect: {
      label: string;
      placeholder: string;
      systemGroup: string;
      customGroup: string;
      roles: {
        owner: string;
        admin: string;
        member: string;
      };
    };
    seatImpact: string;
    invite: string;
    inviting: string;
    success: string;
    error: string;
    cancel?: string;
    invalidEmail?: string;
  };
}

const createInviteUserSchema = (emailError?: string) =>
  z.object({
    email: z.string().email(emailError || 'Invalid email address'),
    roleAssignment: z.custom<RoleAssignment>(
      (value) =>
        typeof value === 'object' &&
        value !== null &&
        'kind' in value &&
        (value.kind === 'builtin' || value.kind === 'custom'),
    ),
  });

type InviteUserFormData = {
  email: string;
  roleAssignment: RoleAssignment;
};

export function InviteUserDialog({
  open,
  onOpenChange,
  workspaceId,
  roles,
  currentUserRole,
  onSuccess,
  dict,
}: InviteUserDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inviteUserSchema = useMemo(
    () => createInviteUserSchema(dict.invalidEmail || 'Invalid email address'),
    [dict.invalidEmail],
  );

  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema as never),
    defaultValues: {
      email: '',
      roleAssignment: {
        kind: 'builtin',
        role: WorkspaceMemberRole.MEMBER,
      },
    },
  });

  const onSubmit = async (data: InviteUserFormData) => {
    setIsSubmitting(true);
    try {
      const body =
        data.roleAssignment.kind === 'custom'
          ? {
              email: data.email.trim(),
              customRoleId: data.roleAssignment.customRoleId,
              acceptRedirectUrl: buildRedirectUrl(
                '/workspace/invitation/accept',
              ),
              declineRedirectUrl: buildRedirectUrl(
                '/workspace/invitation/reject',
              ),
            }
          : {
              email: data.email.trim(),
              role: data.roleAssignment.role,
              acceptRedirectUrl: buildRedirectUrl(
                '/workspace/invitation/accept',
              ),
              declineRedirectUrl: buildRedirectUrl(
                '/workspace/invitation/reject',
              ),
            };

      await WorkspacesApi.inviteUser({
        path: {
          id: workspaceId,
        },
        body,
        headers: {
          origin: typeof window !== 'undefined' ? window.location.origin : '',
          referer: typeof window !== 'undefined' ? window.location.href : '',
        },
      });

      toast.success(dict.success);
      form.reset();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error(dict.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[500px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {dict.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <SettingsGroup>
            <SettingsGroupRows>
              <SettingsGroupRow>
                <div className="space-y-2">
                  <Label htmlFor="email">{dict.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={dict.emailPlaceholder}
                    {...form.register('email')}
                  />
                  {form.formState.errors.email ? (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.email.message}
                    </p>
                  ) : null}
                </div>
              </SettingsGroupRow>

              <SettingsGroupRow separated>
                <div className="space-y-2">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor="invite-role">{dict.roleSelect.label}</Label>
                    {dict.roleDescription ? (
                      <SettingsInfoHint label={dict.roleSelect.label}>
                        {dict.roleDescription}
                      </SettingsInfoHint>
                    ) : null}
                  </div>
                  <Controller
                    control={form.control}
                    name="roleAssignment"
                    render={({ field }) => (
                      <RoleSelect
                        id="invite-role"
                        value={field.value}
                        onChange={field.onChange}
                        roles={roles}
                        currentUserRole={currentUserRole}
                        dict={dict.roleSelect}
                        excludeOwner
                      />
                    )}
                  />
                </div>
              </SettingsGroupRow>
            </SettingsGroupRows>
          </SettingsGroup>

          <Alert>
            <Users className="h-4 w-4" />
            <AlertDescription>{dict.seatImpact}</AlertDescription>
          </Alert>

          <ResponsiveDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {dict.cancel || 'Cancel'}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? dict.inviting : dict.invite}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
